import { useEffect } from 'react';
import { useConnection } from '@xyflow/react';
import type { SharedInFlightEdge } from '../render/awareness-types';

export interface InFlightEdgeBroadcasterProps {
  /** Throttled write of in-flight edge state. Pass null clears the ghost on peers. */
  reportInFlightEdge: (edge: SharedInFlightEdge | null) => void;
}

/**
 * Mounts inside <ReactFlow> and forwards the live useConnection() state to
 * awareness so remote peers can render a ghost edge that follows the dragger's
 * cursor. Covers BOTH fresh-create (drag from a node handle into empty space)
 * and reconnect (drag an existing edge endpoint) — useConnection unifies them.
 *
 * Mirrors the React Flow collaborative-pro example's <Connections> wiring,
 * adapted for our awareness transport (Yjs awareness map vs their persisted
 * Yjs map keyed by userId).
 */
export function InFlightEdgeBroadcaster({ reportInFlightEdge }: InFlightEdgeBroadcasterProps) {
  // Subscribe to the full ConnectionState. The cost is acceptable: this
  // component only mounts once per canvas and re-renders on connection
  // updates (which is exactly what we want — every pointermove during a
  // drag bumps the state, which fires the effect, which calls the
  // throttled writer).
  const connection = useConnection();

  useEffect(() => {
    if (!connection.inProgress) {
      reportInFlightEdge(null);
      return;
    }
    reportInFlightEdge({
      source: connection.fromNode.id,
      sourceType: connection.fromHandle.type,
      sourceHandle: connection.fromHandle.id ?? null,
      target: connection.toNode?.id,
      targetType: connection.toHandle?.type,
      targetHandle: connection.toHandle?.id ?? null,
      position: { x: connection.to.x, y: connection.to.y },
    });
  }, [connection, reportInFlightEdge]);

  return null;
}
